// Modules
import { useEffect, useState, useRef, useMemo } from "react"
import { useFormContext } from "react-hook-form"

// Constants
import { ERROR_MESSAGES } from "~constants/errorMessages"
import {
  ELIGIBILITY_TYPE,
  ELIGIBILITY_TYPE_LABEL,
  HOLDING_PERIOD,
  MAX_PERIOD,
  RULES_LOGIC,
} from "~constants/perks"

// Utils
// Shapes
import {
  createDropdownCollection,
  createDropdownValue,
  SelectOption,
} from "~utils/dropdown"

// Components
import Input from "~app/Input"
import ControlSelect from "~app/ControlSelect"
import {
  HoldingsPeriodSelector,
  RuleLogicPicker,
} from "~styled/redemption-type-picker"
import { WizardColumn, WizardRow, WizardStepContainer } from "~styled/wizard"
import { Security } from "~shapes/portfolio"

interface StepDefaultValues {
  period?: number
  periodUnit?: HOLDING_PERIOD
  ruleLogic?: SelectOption
  initialHoldingsRuleLogic?: SelectOption
  eligibilityType?: SelectOption
  holdings?: number
  noHoldings?: boolean
  holdingsMax?: number
  holdingsMin?: number
  initialHoldings?: number
  initialHoldingsMax?: number
  initialHoldingsMin?: number
  perkSecurities?: string[]
}

interface StepProps {
  isVisible: boolean
  defaultValues?: StepDefaultValues
  securitiesList: Security[]
}

const PerkModalStep3 = ({
  isVisible = true,
  defaultValues,
  securitiesList,
}: StepProps) => {
  const {
    control,
    formState: { errors },
    register,
    watch,
    getValues,
    setValue,
  } = useFormContext()

  const [selectedRule, setSelectedRule] = useState<RULES_LOGIC>()
  const [selectedPeriodUnit, setSelectedPeriodUnit] = useState<HOLDING_PERIOD>()
  const [selectedPeriod, setSelectedPeriod] = useState<number>(0)
  const [selectedInitialHoldingRule, setSelectedInitialHoldingRule] =
    useState<RULES_LOGIC>()
  const [selectedEligibilityType, setSelectedEligibilityType] =
    useState<ELIGIBILITY_TYPE>(ELIGIBILITY_TYPE.SHARES_CONFIGURATION)
  const lastDefaultValuesRef = useRef<string | null>(null)

  // Default eligibilityType to "Shares configuration" for new perks. For
  // existing perks the form is reset with eligibilityType from prefill, so we
  // only seed when the field is unset.
  useEffect(() => {
    if (!getValues("eligibilityType")) {
      setValue(
        "eligibilityType",
        createDropdownValue(
          ELIGIBILITY_TYPE_LABEL[ELIGIBILITY_TYPE.SHARES_CONFIGURATION],
          ELIGIBILITY_TYPE.SHARES_CONFIGURATION,
        ),
      )
    }
  }, [])

  useEffect(() => {
    const option = getValues("eligibilityType")
    if (option?.value) {
      setSelectedEligibilityType(option.value as ELIGIBILITY_TYPE)
    }
  }, [watch("eligibilityType")])

  const isSharesConfig =
    selectedEligibilityType === ELIGIBILITY_TYPE.SHARES_CONFIGURATION

  const eligibilityTypeOptions: SelectOption[] = Object.values(
    ELIGIBILITY_TYPE,
  ).map(value => createDropdownValue(ELIGIBILITY_TYPE_LABEL[value], value))

  useEffect(() => {
    const ruleLogic = getValues("ruleLogic")
    if (ruleLogic) {
      setSelectedRule(ruleLogic.label)
    }
  }, [watch("ruleLogic")])

  // Load securities when both securitiesList and defaultValues are available
  useEffect(() => {
    // Create a key from defaultValues to detect changes
    const defaultValuesKey = defaultValues?.perkSecurities
      ? JSON.stringify([...defaultValues.perkSecurities].sort())
      : null

    // Get current form value to check if securities are already set
    const currentSecurities = getValues("securities")
    const hasSecuritiesInForm =
      currentSecurities &&
      Array.isArray(currentSecurities) &&
      currentSecurities.length > 0

    // Check if we have all required data
    if (
      securitiesList &&
      securitiesList.length > 0 &&
      defaultValues &&
      defaultValues.perkSecurities &&
      defaultValues.perkSecurities.length > 0
    ) {
      // Always set securities if we have the data, regardless of previous state
      // This ensures securities are set even after form reset
      // IMPORTANT: Use the exact option objects from securitiesDropdownOptions to ensure react-select can match them
      const matchingOptions = securitiesDropdownOptions.filter(option =>
        defaultValues.perkSecurities!.includes(option.value as string),
      )

      // Check if the current form value matches what we expect
      const currentSecuritiesKey = hasSecuritiesInForm
        ? JSON.stringify(currentSecurities.map((s: any) => s.value || s).sort())
        : null
      const expectedSecuritiesKey = JSON.stringify(
        [...defaultValues.perkSecurities].sort(),
      )

      const defaultValuesChanged =
        lastDefaultValuesRef.current !== defaultValuesKey
      const securitiesMismatch = currentSecuritiesKey !== expectedSecuritiesKey

      // Only update if securities don't match or if defaultValues changed
      if (defaultValuesChanged || securitiesMismatch) {
        if (matchingOptions.length > 0) {
          // Use the exact option objects from securitiesDropdownOptions so react-select can match them
          setValue("securities", matchingOptions, {
            shouldDirty: true,
            shouldValidate: false,
          })
          lastDefaultValuesRef.current = defaultValuesKey
        }
      }
    } else {
      // Reset if defaultValues is cleared
      if (lastDefaultValuesRef.current !== null) {
        setValue("securities", [])
        lastDefaultValuesRef.current = null
      }
    }
  }, [securitiesList, defaultValues, setValue, getValues])

  useEffect(() => {
    const option = getValues("initialHoldingsRuleLogic")
    if (option) {
      setSelectedInitialHoldingRule(option.label)
    }
  }, [watch("initialHoldingsRuleLogic")])

  useEffect(() => {
    const option = getValues("period")
    setSelectedPeriod(option || 0)
  }, [watch("period")])

  useEffect(() => {
    const option = getValues("periodUnit")
    if (option) {
      setSelectedPeriodUnit(option.label)
    }
  }, [watch("periodUnit")])

  useEffect(() => {
    setInitialHoldingRule()
  }, [selectedPeriod])

  const setInitialHoldingRule = () => {
    if (defaultValues) {
      const { initialHoldingsRuleLogic } = defaultValues

      if (initialHoldingsRuleLogic) {
        setValue("initialHoldingsRuleLogic", initialHoldingsRuleLogic)
        return
      }
    }
    setValue("initialHoldingsRuleLogic", undefined)
  }

  const createRulesLogicDropdown = (): SelectOption[] => {
    const items = Object.keys(RULES_LOGIC).map(k => ({
      id: k,
      name: RULES_LOGIC[k],
    }))
    return createDropdownCollection(items, "name", "id")
  }

  const createHoldingsPeriodDropdown = (): SelectOption[] => {
    const items = Object.keys(HOLDING_PERIOD).map(k => ({
      id: HOLDING_PERIOD[k] == HOLDING_PERIOD.NOW ? null : HOLDING_PERIOD[k],
      name: HOLDING_PERIOD[k],
    }))
    return createDropdownCollection(items, "name", "id")
  }

  // Memoize securities dropdown to ensure stable references for react-select
  const securitiesDropdownOptions = useMemo((): SelectOption[] => {
    if (!securitiesList || securitiesList.length === 0) {
      return []
    }
    const items = securitiesList.map(k => ({
      id: k.code,
      name: k.exchange + "." + k.symbol,
    }))
    const dropdown = createDropdownCollection(items, "name", "id")
    return dropdown
  }, [securitiesList])

  const getInfoMaxPeriod = () => {
    return selectedPeriodUnit ? MAX_PERIOD[selectedPeriodUnit] : 0
  }

  const validatePeriodGivenPeriodUnit = (period: number) => {
    switch (selectedPeriodUnit) {
      case HOLDING_PERIOD.MONTHS:
        return period <= 3
      case HOLDING_PERIOD.WEEKS:
        return period <= 12
      case HOLDING_PERIOD.DAYS:
        return period <= 90
    }
  }

  return (
    <WizardStepContainer className={isVisible ? "visible" : ""}>
      <WizardRow style={{ marginBottom: 30 }}>
        <WizardColumn>
          <ControlSelect
            name={"eligibilityType"}
            label={"*Eligibility"}
            rules={{ required: true }}
            control={control}
            options={eligibilityTypeOptions}
            searchable={false}
            placeholder="Select eligibility"
            errorMessage={
              errors.eligibilityType && ERROR_MESSAGES.FIELD_REQUIRED
            }
          />
        </WizardColumn>
      </WizardRow>

      {isSharesConfig && (
        <>
          <WizardRow style={{ marginBottom: 30 }}>
            <WizardColumn>
              <ControlSelect
                name={"securities"}
                label={"*Securities"}
                rules={{ required: true }}
                control={control}
                options={securitiesDropdownOptions}
                searchable={true}
                placeholder={"Select securities"}
                errorMessage={
                  errors.securities && ERROR_MESSAGES.FIELD_REQUIRED
                }
                isMulti={true}
              />

              <ControlSelect
                name={"ruleLogic"}
                label={"*Holdings Rules logic"}
                rules={{ required: true }}
                control={control}
                options={createRulesLogicDropdown()}
                searchable={false}
                placeholder="Select holdings logic"
                errorMessage={errors.ruleLogic && ERROR_MESSAGES.FIELD_REQUIRED}
              />

              {/* Greater than or equal to */}
              {selectedRule === RULES_LOGIC.GREATER_THAN_OR_EQUAL_TO && (
                <RuleLogicPicker>
                  <span>User's holdings {selectedRule.toLowerCase()}</span>
                  <Input
                    name="holdingsMin"
                    register={register("holdingsMin", {
                      required:
                        selectedRule === RULES_LOGIC.GREATER_THAN_OR_EQUAL_TO,
                    })}
                    placeholder="100"
                    errorMessage={
                      errors.holdingsMin && ERROR_MESSAGES.FIELD_REQUIRED
                    }
                  />
                </RuleLogicPicker>
              )}

              {/* Between */}
              {selectedRule === RULES_LOGIC.BETWEEN && (
                <RuleLogicPicker>
                  <span>User's holdings {selectedRule.toLowerCase()}</span>
                  <Input
                    name="holdingsMin"
                    register={register("holdingsMin", {
                      required: selectedRule === RULES_LOGIC.BETWEEN,
                    })}
                    placeholder="1"
                    errorMessage={
                      errors.holdingsMin && ERROR_MESSAGES.FIELD_REQUIRED
                    }
                  />
                  <Input
                    name="holdingsMax"
                    register={register("holdingsMax", {
                      required: selectedRule === RULES_LOGIC.BETWEEN,
                    })}
                    placeholder="100"
                    errorMessage={
                      errors.holdingsMax && ERROR_MESSAGES.FIELD_REQUIRED
                    }
                  />
                </RuleLogicPicker>
              )}
            </WizardColumn>
          </WizardRow>

          <WizardRow style={{ marginBottom: 30 }}>
            <WizardColumn>
              <HoldingsPeriodSelector>
                <div className="title">Holdings period</div>
                <ControlSelect
                  name={"periodUnit"}
                  rules={{ required: true }}
                  control={control}
                  options={createHoldingsPeriodDropdown()}
                  searchable={false}
                  placeholder="Select timeframe"
                  errorMessage={
                    errors.periodUnit && ERROR_MESSAGES.FIELD_REQUIRED
                  }
                />

                {!!selectedPeriodUnit &&
                  selectedPeriodUnit !== HOLDING_PERIOD.NOW && (
                    <>
                      <div className="info">
                        Maximum of {getInfoMaxPeriod()}
                      </div>
                      <RuleLogicPicker>
                        <span>
                          Period Unit: {HOLDING_PERIOD[selectedPeriodUnit]}
                        </span>
                        <Input
                          name="period"
                          register={register("period", {
                            required: {
                              value: true,
                              message: ERROR_MESSAGES.FIELD_REQUIRED,
                            },
                            validate: value =>
                              validatePeriodGivenPeriodUnit(value) ||
                              ERROR_MESSAGES.EXCEEDS_LIMIT,
                            pattern: {
                              value: /^\d+$/,
                              message: ERROR_MESSAGES.ONLY_NUMBERS,
                            },
                          })}
                          placeholder="1"
                          errorMessage={errors.period && errors.period.message}
                        />
                      </RuleLogicPicker>
                    </>
                  )}

                {/* Initial Holdings */}
                {!!selectedPeriodUnit &&
                  selectedPeriodUnit !== HOLDING_PERIOD.NOW &&
                  selectedPeriod > 0 && (
                    <div style={{ marginTop: 25, marginBottom: 40 }}>
                      <ControlSelect
                        name={"initialHoldingsRuleLogic"}
                        label={"*Initial Holdings Rules logic"}
                        rules={{ required: true }}
                        control={control}
                        options={createRulesLogicDropdown()}
                        searchable={false}
                        placeholder="Select initial holdings logic"
                        errorMessage={
                          errors.initialHoldingsRuleLogic &&
                          ERROR_MESSAGES.FIELD_REQUIRED
                        }
                        marginBottom={"0"}
                      />

                      {/* Greater than or equal to */}
                      {selectedInitialHoldingRule ===
                        RULES_LOGIC.GREATER_THAN_OR_EQUAL_TO && (
                        <RuleLogicPicker>
                          <span>
                            User's initial holdings{" "}
                            {selectedInitialHoldingRule.toLowerCase()}
                          </span>
                          <Input
                            name="initialHoldingsMin"
                            register={register("initialHoldingsMin", {
                              required:
                                selectedInitialHoldingRule ===
                                RULES_LOGIC.GREATER_THAN_OR_EQUAL_TO,
                            })}
                            placeholder="100"
                          />
                        </RuleLogicPicker>
                      )}

                      {/* Between */}
                      {selectedInitialHoldingRule === RULES_LOGIC.BETWEEN && (
                        <RuleLogicPicker>
                          <span>
                            User's initial holdings{" "}
                            {selectedInitialHoldingRule.toLowerCase()}
                          </span>
                          <Input
                            name="initialHoldingsMin"
                            register={register("initialHoldingsMin", {
                              required:
                                selectedInitialHoldingRule ===
                                RULES_LOGIC.BETWEEN,
                            })}
                            placeholder="1"
                          />
                          <Input
                            name="initialHoldingsMax"
                            register={register("initialHoldingsMax", {
                              required:
                                selectedInitialHoldingRule ===
                                RULES_LOGIC.BETWEEN,
                            })}
                            placeholder="100"
                          />
                        </RuleLogicPicker>
                      )}
                    </div>
                  )}
              </HoldingsPeriodSelector>
            </WizardColumn>
          </WizardRow>
        </>
      )}
    </WizardStepContainer>
  )
}

export default PerkModalStep3
